// usecase → feature/procedure 統合（#884）Phase D（移設）。
//
// 旧同期処理では「ターゲット同期時に feature（旧 usecase）を自動生成」する機能が
// test_target_manager 側に無く欠落していた。本モジュールはそれを新規実装として提供する。
//
// 新モデル: 旧「usecase」= View 直下の feature ターゲット（type 空 + mainProcedureId あり）
//   ＋ その main Procedure。feature 自体は名前/説明/親子を持つ薄い箱で、
//   steps/parameters/policies/externalId(=旧usecaseId) は main Procedure の value blob（フラット）に入る。
//
// 生成手順（feature 1件あたり）:
//   1. procedureClient.buildProcedureValueBlob で main procedure の value blob を組み立て、
//      apiClient.createProcedures(...) で main procedure を作成する。
//   2. apiClient.createTestTargets(...) で View 直下に type 空の feature 子ターゲットを作成し、
//      mainProcedureId で作成済み procedure を紐付ける（TestTargetCreateInputWithProcedure 拡張）。
//
// 使用例:
//   import { SSTFApiClient } from "./api";
//   import { generateFeaturesUnderView } from "./featureGenerator";
//
//   const api = new SSTFApiClient(...);
//   const created = await generateFeaturesUnderView(api, workspaceId, projectId, viewTargetId, [
//     {
//       name: "ログインできる",
//       description: "正常系ログイン",
//       steps: [{ step_id: "s1", step: "ログインボタンを押す", expect: "ダッシュボードが表示される" }],
//       parameters: [{ name: "email", default_value: "user@example.com" }],
//       externalId: "uc-0001", // 旧 usecaseId（あれば）
//     },
//   ]);
//   // created[i].featureTarget / created[i].mainProcedure が生成結果。

import {
  SSTFApiClient,
  GraphQLProcedure,
  GraphQLTestTarget,
  CreateProcedureInput,
  TestTargetCreateInputWithProcedure,
  TestTargetUpdateInputWithProcedure,
} from "./api";
import {
  buildProcedureValueBlob,
  echoProcedureUpdate,
  ProcedurePolicy,
} from "./procedureClient";
import { UseCase, UseCaseStep, ParameterValue, Policy } from "../types/TestTarget";

/**
 * 1 feature（＝旧 usecase 1件）の生成入力。
 * name のみ必須。steps/parameters/policies/externalId/parentUsecaseId は
 * main procedure の value blob へ畳み込まれる。
 */
export interface FeatureGenerationInput {
  /** feature ターゲットの表示名（＝画面上の usecase 名）。 */
  name: string;
  /** feature ターゲットの説明。 */
  description?: string;
  /** 手順ステップ（main procedure value.steps）。 */
  steps?: UseCaseStep[];
  /** パラメータ（main procedure value.parameters）。 */
  parameters?: ParameterValue[];
  /** 手順ポリシー（main procedure value.policies へ畳み込む）。 */
  policies?: ProcedurePolicy[];
  /** 旧 usecaseId。指定時は main procedure value.externalId に保持する。 */
  externalId?: string | null;
  /** 親 usecase 参照（UC 親子ツリー用）。main procedure value.parentUsecaseId に保持する。 */
  parentUsecaseId?: string | null;
  /**
   * main procedure の名前。未指定時は feature 名を流用する。
   * （画面上は feature 名が usecase 名として扱われる。）
   */
  procedureName?: string;
}

/** 生成した feature ターゲットと、その main procedure のペア。 */
export interface GeneratedFeature {
  featureTarget: GraphQLTestTarget;
  mainProcedure: GraphQLProcedure;
}

/** generateFeaturesUnderView の任意オプション。 */
export interface GenerateFeaturesOptions {
  /**
   * feature 子ターゲットに割り当てる型 ID。feature は type-less のため既定は null。
   * 空文字は UUID キャスト不可（DB エラー）になるため null を送る。
   */
  featureTypeId?: string | null;
  /**
   * feature 群の先頭 displayOrder。以降 +1 ずつ採番する（既定 1）。
   * View 内の既存 feature 末尾に追加したい場合は末尾+1 を渡す。
   */
  startDisplayOrder?: number;
}

/**
 * View 直下に feature ターゲット群（＋各 main procedure）を生成する。
 *
 * feature ごとに「main procedure 作成 → feature 子ターゲット作成（mainProcedureId 紐付け）」の
 * 2 段で作成する。1 件ごとに逐次実行し、procedure と feature の対応を確実に取る。
 * 途中で失敗した場合は例外を送出する（それまでに作成済みのものはロールバックしない）。
 *
 * @param apiClient   SSTF API クライアント
 * @param workspaceId ワークスペース UUID
 * @param projectId   プロジェクト UUID
 * @param viewTargetId 親 View ターゲットの UUID（feature はこの直下に作られる）
 * @param features    生成する feature の入力配列
 * @param options     型 ID / 開始 displayOrder 等の任意設定
 * @returns 生成された feature ターゲットと main procedure のペア配列（入力順）
 */
export async function generateFeaturesUnderView(
  apiClient: SSTFApiClient,
  workspaceId: string,
  projectId: string,
  viewTargetId: string,
  features: FeatureGenerationInput[],
  options: GenerateFeaturesOptions = {}
): Promise<GeneratedFeature[]> {
  const featureTypeId = options.featureTypeId ?? null;
  const startDisplayOrder = options.startDisplayOrder ?? 1;

  const generated: GeneratedFeature[] = [];

  for (let i = 0; i < features.length; i += 1) {
    const feature = features[i];
    const displayOrder = startDisplayOrder + i;
    const procedureName = feature.procedureName ?? feature.name;

    // 1. main procedure の value blob を組み立てて作成する。
    //    steps/parameters は value 直下、externalId/status/parentUsecaseId/policies も value 直下へフラットに置く。
    //    （type は value に書かない設計。読み取り側 composeUsecase が既定 'main' を補う。）
    const value = buildProcedureValueBlob({
      steps: feature.steps,
      parameters: feature.parameters,
      externalId: feature.externalId ?? undefined,
      status: "draft",
      parentUsecaseId: feature.parentUsecaseId ?? undefined,
      policies: feature.policies,
    });

    const mainProcedureInput: CreateProcedureInput = {
      name: procedureName,
      displayOrder,
      inheritFromParent: false,
      ...(value !== undefined ? { value } : {}),
    };

    // SSTF の CreateTestTargetInput は mainProcedureId(ID リンク)を受け付けず、
    // ネストした mainProcedure(CreateProcedureInput)で feature＋procedure を同時作成する。
    const targetInput: TestTargetCreateInputWithProcedure = {
      name: feature.name,
      displayOrder,
      typeId: featureTypeId,
      description: feature.description,
      parentTestTargetId: viewTargetId,
      // params は必須（JSON 文字列）。feature 本体は薄い箱なので空オブジェクトを渡す。
      params: "{}",
      mainProcedure: mainProcedureInput,
    };

    const createdTargets = await apiClient.createTestTargets(
      workspaceId,
      projectId,
      [targetInput]
    );
    const featureTarget = createdTargets[0];
    if (!featureTarget) {
      throw new Error(
        `feature ターゲットの作成に失敗しました（feature: ${feature.name}）`
      );
    }

    // 作成レスポンスは mainProcedure { id } のみ返すため、返却用に最小の Procedure を組み立てる。
    const mainProcedure: GraphQLProcedure = {
      id: featureTarget.mainProcedure?.id ?? "",
      workspaceId,
      projectId,
      name: procedureName,
      displayOrder,
      inheritFromParent: false,
      value: value ?? null,
    };

    generated.push({ featureTarget, mainProcedure });
  }

  return generated;
}

// ==== 旧 UseCase 編集 UI（UseCaseEditModal）からの保存 ====

/** UseCase.policies(Policy[]) を Procedure value 用 ProcedurePolicy[] へ変換する。 */
export function toProcedurePolicies(
  policies?: Policy[]
): ProcedurePolicy[] | undefined {
  if (!policies) return undefined;
  return policies.map((p) => ({
    policyId: p.policy_id,
    name: p.name,
    description: p.description,
    status: p.status,
    changes: p.changes,
    parameters: p.parameters,
  }));
}

/** 既存 feature の識別情報（更新時に必要な完全置換エコー用フィールド）。 */
export interface SaveFeatureExisting {
  featureTargetId: string;
  procedureId: string;
  /** feature は type-less のため通常 null。 */
  featureTypeId: string | null;
  /** update の完全置換で既存 value を維持するためにエコーする値(JSON文字列)。 */
  featureValue: string | null;
  displayOrder: number;
}

/**
 * 旧 UseCase 編集 UI（UseCaseEditModal）の onSave(useCase) を受けて、
 * feature ターゲット＋main procedure として SSTF に保存する。
 * existing があれば更新（updateProcedures + updateTestTargets）、なければ新規作成。
 * UseCase の steps/parameters/policies/status/usecase_id(=externalId) を main procedure value に畳み込む。
 */
export async function saveFeatureFromUseCase(
  apiClient: SSTFApiClient,
  workspaceId: string,
  projectId: string,
  viewTargetId: string,
  useCase: UseCase,
  existing?: SaveFeatureExisting | null,
  newDisplayOrder?: number
): Promise<GeneratedFeature> {
  const featureName = useCase.title || useCase.name || "無題の機能";

  // 更新: 既存 procedure/feature を完全置換で書き換える。
  if (existing?.procedureId && existing.featureTargetId) {
    const [existingProc] = await apiClient.getProcedures(
      workspaceId,
      projectId,
      [existing.procedureId]
    );
    if (!existingProc) {
      throw new Error(
        `更新対象の手順が見つかりません（procedureId: ${existing.procedureId}）`
      );
    }
    const value = buildProcedureValueBlob(
      {
        steps: useCase.steps,
        parameters: useCase.parameters,
        externalId: useCase.usecase_id || undefined,
        status: useCase.status,
        policies: toProcedurePolicies(useCase.policies),
      },
      existingProc.value ?? null
    );
    const updatedProcs = await apiClient.updateProcedures(
      workspaceId,
      projectId,
      [echoProcedureUpdate(existingProc, { name: featureName, value })]
    );
    const mainProcedure = updatedProcs[0] ?? existingProc;

    const updateInput: TestTargetUpdateInputWithProcedure = {
      testTargetId: existing.featureTargetId,
      name: featureName,
      displayOrder: existing.displayOrder,
      // feature は type-less。null を送る（空文字は UUID キャスト不可で DB エラーになる）。
      typeId: existing.featureTypeId,
      description: useCase.description || undefined,
      parentTestTargetId: viewTargetId,
      // 完全置換 update のため、feature の既存 value を維持する（{} で上書きしない）。
      params: existing.featureValue ?? "{}",
      mainProcedureId: existing.procedureId,
    };
    const updatedTargets = await apiClient.updateTestTargets(
      workspaceId,
      projectId,
      [updateInput]
    );
    return { featureTarget: updatedTargets[0], mainProcedure };
  }

  // 新規作成: generateFeaturesUnderView を単体で使う。
  const [created] = await generateFeaturesUnderView(
    apiClient,
    workspaceId,
    projectId,
    viewTargetId,
    [
      {
        name: featureName,
        description: useCase.description,
        steps: useCase.steps,
        parameters: useCase.parameters,
        policies: toProcedurePolicies(useCase.policies),
        externalId: useCase.usecase_id || undefined,
      },
    ],
    { startDisplayOrder: newDisplayOrder }
  );
  return created;
}
